<template>
  <div class="flow-chart-wrap">
    <div ref="chart" class="flow-chart" :style="{ height: chartHeight + 'px' }"></div>
  </div>
</template>

<script>
import saveAs from "file-saver";
import { getStatusMeta, normalizeFlowItem } from "./traceUtils";

export default {
  name: "LogisticsFlowChart",
  props: {
    productName: {
      type: String,
      default: "",
    },
    logisticsCode: {
      type: String,
      default: "",
    },
    currentStatusText: {
      type: String,
      default: "",
    },
    currentStatus: {
      type: [String, Number],
      default: 0,
    },
    items: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      chart: null,
      chartHeight: 300,
      ready: false,
      resizeTimer: null,
      measureContext: null,
    };
  },
  watch: {
    items: {
      deep: true,
      handler() {
        this.queueRender();
      },
    },
    productName() {
      this.queueRender();
    },
    logisticsCode() {
      this.queueRender();
    },
    currentStatusText() {
      this.queueRender();
    },
    currentStatus() {
      this.queueRender();
    },
  },
  mounted() {
    this.ready = true;
    this.renderChart();
    window.addEventListener("resize", this.handleResize);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.handleResize);
    clearTimeout(this.resizeTimer);
    if (this.chart) {
      this.chart.dispose();
      this.chart = null;
    }
  },
  methods: {
    queueRender() {
      if (!this.ready) {
        return;
      }
      this.$nextTick(this.renderChart);
    },
    handleResize() {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => {
        this.renderChart();
      }, 120);
    },
    measureTextWidth(value, font) {
      if (!this.measureContext) {
        const canvas = document.createElement("canvas");
        this.measureContext = canvas.getContext("2d");
      }
      if (!this.measureContext) {
        return String(value || "").length * 14;
      }
      this.measureContext.font = font;
      return this.measureContext.measureText(String(value || "")).width;
    },
    wrapTextByWidth(value, maxWidth, font) {
      const text = String(value || "--");
      const lines = [];
      let line = "";

      Array.from(text).forEach((character) => {
        if (character === "\n") {
          lines.push(line || " ");
          line = "";
          return;
        }

        const candidate = `${line}${character}`;
        if (line && this.measureTextWidth(candidate, font) > maxWidth) {
          lines.push(line);
          line = character;
        } else {
          line = candidate;
        }
      });

      if (line) {
        lines.push(line);
      }
      return lines.length ? lines : ["--"];
    },
    createText(x, y, text, font, fill, extraStyle) {
      return {
        type: "text",
        silent: true,
        style: Object.assign(
          {
            x,
            y,
            text,
            font,
            fill,
            textAlign: "left",
            textVerticalAlign: "top",
          },
          extraStyle || {}
        ),
      };
    },
    buildLayout(chartWidth) {
      const width = Math.max(chartWidth, 1040);
      const nodeWidth = Math.min(990, width - 48);
      const nodeX = Math.round((width - nodeWidth) / 2);
      const normalizedItems = this.items.map(normalizeFlowItem);
      const titleFont = "600 20px Microsoft YaHei, sans-serif";
      const statusFont = "18px Microsoft YaHei, sans-serif";
      const statusText = this.currentStatusText || "--";
      const statusMeta = getStatusMeta(this.currentStatus);
      const statusWidth = Math.ceil(
        this.measureTextWidth(statusText, statusFont) + 24
      );
      const statusHeight = 38.4;
      const titleGap = 12;
      const titleContentWidth = width - 48;
      const productMaxWidth = Math.max(
        120,
        titleContentWidth - statusWidth - titleGap
      );
      const productLines = this.wrapTextByWidth(
        this.productName || "未命名产品",
        productMaxWidth,
        titleFont
      );
      const titleTop = 20;
      const titleRowHeight = 42;
      const logisticsY = titleTop + productLines.length * titleRowHeight + 8;
      const nodes = [];
      let cursorY = logisticsY + 42;

      normalizedItems.forEach((item) => {
        const columnGap = 16;
        const innerPadding = 24;
        const detailDefinitions = [
          {
            title: item.merchantLabel,
            value: item.merchant,
            weight: 2,
            font: "600 18px Microsoft YaHei, sans-serif",
            lineHeight: 26,
          },
          {
            title: "日期",
            value: item.createdAt,
            weight: 1.35,
            font: "14px Microsoft YaHei, sans-serif",
            lineHeight: 22,
          },
        ];

        if (item.warehouse) {
          detailDefinitions.push({
            title: "仓库",
            value: item.warehouse,
            weight: 1,
            font: "14px Microsoft YaHei, sans-serif",
            lineHeight: 22,
          });
        }

        detailDefinitions.push({
          title: "操作人",
          value: item.operator,
          weight: 1.25,
          font: "14px Microsoft YaHei, sans-serif",
          lineHeight: 22,
        });

        const totalWeight = detailDefinitions.reduce(
          (sum, column) => sum + column.weight,
          0
        );
        const availableWidth =
          nodeWidth -
          innerPadding * 2 -
          columnGap * (detailDefinitions.length - 1);
        const rightEdge = nodeX + nodeWidth - innerPadding;
        let columnX = nodeX + innerPadding;
        const columns = detailDefinitions.map((column, index) => {
          const isLast = index === detailDefinitions.length - 1;
          const columnWidth = isLast
            ? rightEdge - columnX
            : Math.floor((availableWidth * column.weight) / totalWeight);
          const lines = this.wrapTextByWidth(
            column.value,
            Math.max(columnWidth, 40),
            column.font
          );
          const result = Object.assign({}, column, {
            x: columnX,
            width: columnWidth,
            lines,
          });
          columnX += columnWidth + columnGap;
          return result;
        });
        const maxContentHeight = Math.max(
          ...columns.map((column) => column.lines.length * column.lineHeight)
        );
        const detailsY = cursorY + 54;
        const nodeHeight = 54 + 22 + maxContentHeight + 22;
        nodes.push({
          item,
          x: nodeX,
          y: cursorY,
          width: nodeWidth,
          height: nodeHeight,
          columns,
          detailsY,
        });
        cursorY += nodeHeight + 54;
      });

      const height = nodes.length ? cursorY - 14 : 300;
      const graphics = [];
      const centerX = Math.round(width / 2);

      productLines.forEach((line, index) => {
        const rowTop = titleTop + index * titleRowHeight;
        const isFirstLine = index === 0;

        if (!isFirstLine) {
          graphics.push(
            this.createText(centerX, rowTop + 7, line, titleFont, "#303133", {
              textAlign: "center",
            })
          );
          return;
        }

        const productLineWidth = Math.ceil(
          this.measureTextWidth(line, titleFont)
        );
        const groupWidth = productLineWidth + titleGap + statusWidth;
        const groupX = Math.round((width - groupWidth) / 2);
        const statusX = groupX;
        const productX = statusX + statusWidth + titleGap;

        graphics.push(
          this.createText(productX, rowTop + 7, line, titleFont, "#303133")
        );
        graphics.push({
          type: "rect",
          silent: true,
          shape: {
            x: statusX,
            y: rowTop,
            width: statusWidth,
            height: statusHeight,
            r: 4.8,
          },
          style: {
            fill: statusMeta.backgroundColor,
            stroke: statusMeta.color,
            lineWidth: 1,
          },
        });
        graphics.push(
          this.createText(
            statusX + statusWidth / 2,
            rowTop + statusHeight / 2,
            statusText,
            statusFont,
            statusMeta.color,
            {
              textAlign: "center",
              textVerticalAlign: "middle",
            }
          )
        );
      });
      graphics.push(
        this.createText(
          centerX,
          logisticsY,
          `物流码：${this.logisticsCode || "--"}`,
          "14px Microsoft YaHei, sans-serif",
          "#909399",
          { textAlign: "center" }
        )
      );

      nodes.forEach((node, index) => {
        if (index > 0) {
          const previousNode = nodes[index - 1];
          const lineStart = previousNode.y + previousNode.height;
          const lineEnd = node.y - 10;
          graphics.push({
            type: "line",
            silent: true,
            shape: {
              x1: centerX,
              y1: lineStart,
              x2: centerX,
              y2: lineEnd,
            },
            style: {
              stroke: "#b8c0cc",
              lineWidth: 2,
            },
          });
          graphics.push({
            type: "polygon",
            silent: true,
            shape: {
              points: [
                [centerX - 6, lineEnd - 7],
                [centerX + 6, lineEnd - 7],
                [centerX, node.y - 1],
              ],
            },
            style: {
              fill: "#b8c0cc",
            },
          });
        }
      });

      nodes.forEach((node) => {
        const meta = getStatusMeta(node.item.status);
        const typeFont = "600 16px Microsoft YaHei, sans-serif";
        const typeX = node.x + 24;
        graphics.push({
          type: "rect",
          silent: true,
          shape: {
            x: node.x,
            y: node.y,
            width: node.width,
            height: node.height,
            r: 6,
          },
          style: {
            fill: meta.backgroundColor,
            stroke: meta.color,
            lineWidth: 2,
            shadowBlur: 5,
            shadowColor: "rgba(0, 0, 0, 0.08)",
            shadowOffsetY: 2,
          },
        });
        graphics.push({
          type: "rect",
          silent: true,
          shape: {
            x: node.x,
            y: node.y,
            width: 6,
            height: node.height,
            r: [6, 0, 0, 6],
          },
          style: {
            fill: meta.color,
          },
        });
        graphics.push(
          this.createText(
            typeX,
            node.y + 20,
            node.item.statusLabel,
            typeFont,
            meta.color
          )
        );
        graphics.push(
          this.createText(
            typeX + this.measureTextWidth(node.item.statusLabel, typeFont) + 5,
            node.y + 20,
            `(单号:${node.item.orderNo})`,
            typeFont,
            meta.color
          )
        );
        node.columns.forEach((column) => {
          graphics.push(
            this.createText(
              column.x,
              node.detailsY,
              column.title,
              "13px Microsoft YaHei, sans-serif",
              "#909399"
            )
          );
          graphics.push(
            this.createText(
              column.x,
              node.detailsY + 22,
              column.lines.join("\n"),
              column.font,
              "#303133",
              { lineHeight: column.lineHeight }
            )
          );
        });
      });

      return { height, graphics };
    },
    renderChart() {
      if (!this.$refs.chart || !this.items.length) {
        return;
      }

      const width = this.$refs.chart.clientWidth || 760;
      const layout = this.buildLayout(width);
      this.chartHeight = layout.height;

      this.$nextTick(() => {
        if (!this.chart) {
          this.chart = this.$echarts.init(this.$refs.chart);
        }
        this.chart.resize({ height: layout.height });
        this.chart.clear();
        this.chart.setOption(
          {
            animation: false,
            backgroundColor: "#ffffff",
            graphic: layout.graphics,
          },
          true
        );
      });
    },
    download() {
      if (!this.chart || !this.items.length) {
        return false;
      }

      const dataUrl = this.chart.getDataURL({
        type: "png",
        pixelRatio: 2,
        backgroundColor: "#ffffff",
      });
      const encodedData = dataUrl.split(",")[1] || "";
      const binaryData = window.atob(encodedData);
      const bytes = new Uint8Array(binaryData.length);
      for (let index = 0; index < binaryData.length; index += 1) {
        bytes[index] = binaryData.charCodeAt(index);
      }
      const imageBlob = new Blob([bytes], { type: "image/png" });
      const now = new Date();
      const pad = (value) => (value < 10 ? `0${value}` : String(value));
      const timestamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(
        now.getDate()
      )}-${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
      const identity = this.logisticsCode || this.productName || "物流轨迹";
      const safeIdentity = identity.replace(/[\\/:*?"<>|]+/g, "-");
      saveAs(imageBlob, `物流轨迹-${safeIdentity}-${timestamp}.png`);
      return true;
    },
  },
};
</script>

<style lang="scss" scoped>
.flow-chart-wrap {
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  background: #fff;
}

.flow-chart {
  width: 100%;
  min-width: 1040px;
  min-height: 300px;
}
</style>
